import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import styles from './OrganizationSwitcher.module.css';

import { logger } from '../../utils/logger';

export const OrganizationSwitcher: React.FC = () => {
  const { currentOrganization, organizations, switchOrganization, loading } = useOrganization();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // True when the user has admin/owner access to at least one organization — gates the
  // "Manage organizations" footer that links to /admin/organizations (rename, edit, delete).
  const canManageAnyOrg = organizations.some((org) => {
    const role = (org as any).user_role;
    return role === 'owner' || role === 'admin';
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSwitch = async (orgId: string) => {
    if (orgId === currentOrganization?.id) {
      setIsOpen(false);
      return;
    }

    try {
      await switchOrganization(orgId);
    } catch (error) {
      logger.error('Failed to switch organization:', error);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'owner':
        return styles.roleOwner;
      case 'admin':
        return styles.roleAdmin;
      case 'member':
        return styles.roleMember;
      case 'viewer':
        return styles.roleViewer;
      default:
        return styles.roleMember;
    }
  };

  // Show loading state while organization is being loaded
  if (loading && !currentOrganization) {
    return (
      <div className={styles.container}>
        <div className={styles.trigger} style={{ opacity: 0.6, cursor: 'wait' }}>
          <div className={styles.currentOrg}>
            <div className={styles.orgIcon}>...</div>
            <div className={styles.orgInfo}>
              <div className={styles.orgName}>Loading...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no organization after loading
  if (!currentOrganization) {
    return null;
  }

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        aria-label="Switch organization"
        aria-expanded={isOpen}
      >
        <div className={styles.currentOrg}>
          <div className={styles.orgIcon}>
            {currentOrganization.name.charAt(0).toUpperCase()}
          </div>
          <div className={styles.orgInfo}>
            <div className={styles.orgName} title={currentOrganization.name}>{currentOrganization.name}</div>
            <div className={styles.orgRole}>
              {(currentOrganization as any).user_role || 'member'}
            </div>
          </div>
        </div>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <span>Switch Organization</span>
            <span className={styles.orgCount}>{organizations.length}</span>
          </div>

          <div className={styles.orgList}>
            {organizations.length === 0 && (
              <div className={styles.emptyState}>No organizations available</div>
            )}
            {organizations.map((org) => {
              const isActive = org.id === currentOrganization.id;
              const userRole = (org as any).user_role || 'member';

              return (
                <button
                  key={org.id}
                  className={`${styles.orgItem} ${isActive ? styles.orgItemActive : ''}`}
                  onClick={() => handleSwitch(org.id)}
                  disabled={loading}
                >
                  <div className={styles.orgItemIcon}>
                    {org.name.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.orgItemInfo}>
                    <div className={styles.orgItemName}>{org.name}</div>
                    <div className={styles.orgItemMeta}>
                      <span className={`${styles.roleBadge} ${getRoleBadgeColor(userRole)}`}>
                        {userRole}
                      </span>
                      <span className={styles.memberCount}>
                        {org.member_count} {org.member_count === 1 ? 'member' : 'members'}
                      </span>
                    </div>
                  </div>
                  {isActive && (
                    <svg
                      className={styles.checkIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3333 4L6 11.3333L2.66667 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          {canManageAnyOrg && (
            <button
              type="button"
              className={styles.manageLink}
              onClick={() => {
                setIsOpen(false);
                navigate('/admin/organizations');
              }}
            >
              Manage organizations
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

